import { Column } from "primereact/column";
import { plansApi } from "../../services/plans/plansApi";
import { Skeleton } from "primereact/skeleton";
import { DataTable } from "../../components/data/DataTable";
import { Link, useNavigate } from "react-router-dom";
import { PlanResponse } from "../../models/dtos";
import { Button } from "primereact/button";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import Error from "../../components/common/Error";
import { useSelector } from "react-redux";
import { getPlansFilters } from "../../services/plans/plansSlice";
import { Toolbar } from "primereact/toolbar";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../hooks/redux";
import { setContextSidebar } from "../../services/base/layoutSlice";
import { Toast } from "primereact/toast";

const PlansList = () => {
  const plansFilters = useSelector(getPlansFilters);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const toast = useRef<Toast>(null);
  const dt = useRef<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(setContextSidebar(true));

    return () => {
      dispatch(setContextSidebar(false));
    };
  }, [dispatch]);

  const {
    data: plans,
    error,
    isLoading,
  } = plansApi.useGetPlansQuery({
    byFaculty: plansFilters.faculties.map((x) => ({
      id: x.facultyId,
    })),
    bySpeciality: plansFilters.specialities.map((x) => ({
      id: x.specialityId,
    })),
  });

  const [createPlan] = plansApi.useCreatePlanFromCopyMutation();
  const [deletePlan] = plansApi.useDeletePlanMutation();

  const planCodeTemplate = (rowData: PlanResponse) => {
    return (
      <div className="flex align-items-center justify-content-between">
        <span>{rowData.planId}</span>
        <Link to={`/plans/${rowData.planId}`}>
          <Button icon="pi pi-book"></Button>
        </Link>
      </div>
    );
  };

  const rowClassName = (rowData: PlanResponse) => {
    return rowData.isActive === false ? "opacity-60" : "";
  };

  const handleExport = () => {
    if (dt.current) {
      dt.current.exportCSV({ selectionOnly: true });
    }
  };

  const createPlanFromCopy = async (planId: number) => {
    setLoading(true);
    try {
      const response = await createPlan(planId).unwrap();
      setLoading(false);
      navigate(`/plans/${response.data}`);
    } catch (error) {
      setLoading(false);
      toast?.current?.show({ severity: "error", summary: "Грешка", detail: "Възникна грешка" });
    }
  };

  const renderCreatePlanButton = (rowData: PlanResponse) => (
    <Button
      label=""
      icon="pi pi-plus"
      onClick={() => {
        createPlanFromCopy(rowData.planId);
      }}
      disabled={loading}
    />
  );

  const handleDeletePlan = async (planId: number) => {
    setLoading(true);
    try {
      const response = await deletePlan(planId).unwrap();
      if (response.data === true) {
        toast.current?.show({ severity: "success", summary: "Изтриване", detail: "Учебният план е изтрит." });
      } else {
        toast?.current?.show({
          severity: "error",
          summary: "Грешка",
          detail: "Възникна грешка по време на изтриването",
        });
      }
    } catch (error) {
      toast?.current?.show({ severity: "error", summary: "Грешка", detail: "Възникна грешка по време на изтриването" });
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (event: React.MouseEvent<HTMLElement>, item: number) => {
    confirmPopup({
      target: event.currentTarget as HTMLElement,
      message: "Сигурни ли сте, че искате да изтриете този запис?",
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Да",
      rejectLabel: "Не",
      accept: () => handleDeletePlan(item),
    });
  };

  const renderDeletePlanButton = (rowData: PlanResponse) => (
    <Button
      label=""
      icon="pi pi-trash"
      className="p-button-danger"
      onClick={(e) => confirmDelete(e, rowData.planId)}
      disabled={loading}
    />
  );

  const topButtons = (
    <>
      <Button label="Добави план" icon="pi pi-plus" severity="success" onClick={() => navigate("/plans/create")} />
    </>
  );

  const extraButtons = (
    <>
      <Button label="Експорт CSV" icon="pi pi-upload" severity="secondary" onClick={handleExport} />
    </>
  );

  if (isLoading) return <Skeleton className="w-full h-2rem" />;
  if (error) return <Error error={error} />;

  return (
    <div className="sticky top-0">
      <h1 className="mb-4 mt-0 text-4xl font-light">Учебни планове</h1>
      <Toolbar start={topButtons} end={extraButtons} />
      <ConfirmPopup />
      <Toast ref={toast} />
      <DataTable ref={dt} value={plans?.data ?? []} emptyMessage="Не бяха открити планове." rowClassName={rowClassName}>
        <Column field="planId" header="ID" body={planCodeTemplate} sortable filter filterMatchMode="contains"></Column>
        <Column field="code" header="Код" sortable filter filterMatchMode="contains"></Column>
        <Column
          field="startAcademicYearYearNumber"
          header="Начална академична година"
          sortable
          filter
          filterMatchMode="contains"
        ></Column>
        <Column
          field="endAcademicYearYearNumber"
          header="Крайна академична година"
          sortable
          filter
          filterMatchMode="contains"
        ></Column>
        <Column field="specialityName" header="Специалност" sortable filter filterMatchMode="contains"></Column>
        <Column field="degreeName" header="Степен" sortable filter filterMatchMode="contains"></Column>
        <Column
          field="specialityModeName"
          header="Режим на обучение"
          sortable
          filter
          filterMatchMode="contains"
        ></Column>
        <Column field="planTypeName" header="Тип на плана" sortable filter filterMatchMode="contains"></Column>
        <Column field="approvalDate" header="Дата на одобрение" sortable filter filterMatchMode="contains"></Column>
        <Column body={renderCreatePlanButton} header="Създай от копие"></Column>
        <Column body={renderDeletePlanButton} header="Изтрий план"></Column>
      </DataTable>
    </div>
  );
};

export default PlansList;
